import { useState } from 'react'
import ModalComponent from '..'
import {
  useFeatureEventMutation,
  useGetFeaturedEventsQuery,
} from '../../../redux-toolkits/events/events.slice'
import { FulfilmentCentreRole } from '../../../redux-toolkits/fulfilment-centres/fulfilmentCentres.type'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'

/**
 * Picks every storefront an event is featured on: Good Online (the parent)
 * and any child centres. Ticked storefronts start as the ones it is featured
 * on now; saving features it on the ticked ones and removes it from the rest.
 */
export function FeatureEventModal({
  event,
  open,
  setOpen,
  onClose,
}: {
  event?: {
    _id: string
    name: string
    eventTicketSettings?: { venueCentre?: string | null }
  }
  open: boolean
  setOpen: (open: boolean) => void
  onClose: () => void
}) {
  const { data: centres } = useGetFulfilmentCentersQuery({})
  const { data: featured } = useGetFeaturedEventsQuery()
  const [featureEvent, { isLoading }] = useFeatureEventMutation()

  const venue = event?.eventTicketSettings?.venueCentre
  // The list already puts the parent (Good Online) first.
  const storefronts = (centres?.data.docs ?? [])
    .filter((centre) => centre.isActive !== false)
    .map((centre) => {
      const isParent = centre.role === FulfilmentCentreRole.PARENT
      return {
        id: centre._id,
        name: isParent ? `${centre.name} (parent)` : centre.name,
        // A child storefront only lists events at its venue or with no venue.
        listsEvent: isParent || !venue || venue === centre._id,
        current: featured?.data.find(
          (item) => item.fulfilmentCentre?._id === centre._id,
        )?.event,
      }
    })

  const featuredOn = storefronts
    .filter((storefront) => storefront.current?._id === event?._id)
    .map((storefront) => storefront.id)
  const [picked, setPicked] = useState<string[]>()
  const selected = picked ?? featuredOn

  const toggle = (id: string, checked: boolean) =>
    setPicked(
      checked ? [...selected, id] : selected.filter((item) => item !== id),
    )

  const close = () => {
    setPicked(undefined)
    onClose()
  }

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={close}
      title={`Feature ${event?.name ?? 'event'}`}
      fullScreen={false}
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <p className="font-medium text-base">Storefronts</p>
          {storefronts.map((storefront) => {
            const checked = selected.includes(storefront.id)
            const current = storefront.current
            let note = 'Nothing featured'
            if (!storefront.listsEvent)
              note = 'Held at another centre, so not listed here'
            else if (current?._id === event?._id) note = 'Featured here now'
            else if (current)
              note = `${checked ? 'Replaces' : 'Featured now'}: ${current.name}`

            return (
              <Checkbox
                key={storefront.id}
                className="!w-full"
                checkStatus={checked}
                disabled={!storefront.listsEvent}
                handleChange={(status) => toggle(storefront.id, status)}
                text={
                  <span className="flex flex-col">
                    <span className="font-medium">{storefront.name}</span>
                    <span className="text-sm text-sec-black">{note}</span>
                  </span>
                }
                textPosition="left"
              />
            )
          })}
        </div>
        <Button
          type="button"
          primary
          label="Save featured"
          loading={isLoading}
          disabled={!event || picked === undefined || isLoading}
          onClick={async () => {
            if (!event) return
            const response = await featureEvent({
              eventId: event._id,
              fulfilmentCentreIds: selected,
            })
            if ('data' in response && response.data?.success) close()
          }}
        />
      </div>
    </ModalComponent>
  )
}
